import { z } from 'zod';

/**
 * Workspace credit balance (iter 85).
 *
 * Per-workspace counter of remaining generation credits. Read by the
 * Photo Studio's credits chip + Fit-to-budget math; mutated server-side
 * by the generation route (deduction on success) and by future billing
 * flows (top-up + plan changes — deferred to credit-pack ADR).
 *
 * Plan tier is informational — surfaces "Free / Pro / Enterprise"
 * labels in the UI. The actual entitlement check happens via
 * `balance >= cost` on every generation, not via the tier string.
 *
 * `nextRenewalAt` is the ISO timestamp when the monthly free-tier
 * credits refill, or null for plans without renewal.
 */

export const PLAN_TIERS = ['free', 'pro', 'enterprise'] as const;
export const PlanTierSchema = z.enum(PLAN_TIERS);
export type PlanTier = z.infer<typeof PlanTierSchema>;

export const CreditBalanceSchema = z.object({
  workspaceId: z.string().min(1),
  balance: z.number().int().nonnegative(),
  planTier: PlanTierSchema,
  nextRenewalAt: z.string().datetime().nullable(),
});
export type CreditBalance = z.infer<typeof CreditBalanceSchema>;

/**
 * `GET /api/credits/balance` response. Single-record shape (one balance
 * per workspace). 404 isn't possible — every workspace has an implicit
 * balance, defaulting to the free-tier starter pack when no record exists.
 */
export const CreditBalanceResponseSchema = CreditBalanceSchema;
export type CreditBalanceResponse = z.infer<typeof CreditBalanceResponseSchema>;

/**
 * Default credits granted to a workspace when no Firestore record exists
 * yet. Matches the legacy free-tier starter pack so the UX is consistent
 * with the existing app — users see "16 credits" on first studio open.
 */
export const FREE_TIER_DEFAULT_BALANCE = 16;
